import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import WeddingForm from "../components/WeddingForm";
import Loading from "../components/Loading";
import ErrorMessage from "../components/ErrorMessage";
import { getWedding, updateWedding, uploadCouplePhoto } from "../api/weddings";
import { CreateWeddingPayload, Wedding } from "../types";
import { useAuth } from "../context/AuthContext";
import {
  COUPLE_PHOTO_ACCEPT,
  COUPLE_PHOTO_ACCEPTED_LABEL,
  fileToBase64,
  isHeicLike,
  prepareCouplePhoto,
} from "../utils/couplePhoto";

const photoErrorMessage = (err: unknown): string => {
  const raw = err instanceof Error ? err.message : "Failed to upload photo";
  const lower = raw.toLowerCase();
  if (
    lower.includes("type") ||
    lower.includes("supported") ||
    lower.includes("too large") ||
    lower.includes("heic") ||
    lower.includes("convert") ||
    lower.includes("resize") ||
    lower.includes("read this image")
  ) {
    return raw;
  }
  return `The server couldn’t save this photo. ${raw} Try a smaller JPEG, or save your listing first and upload the photo again.`;
};

const EditWedding: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [wedding, setWedding] = useState<Wedding | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photoPhase, setPhotoPhase] = useState<"idle" | "converting" | "uploading">("idle");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [photoError, setPhotoError] = useState("");
  const [photoSuccess, setPhotoSuccess] = useState("");

  useEffect(() => {
    if (!id || authLoading) return;

    if (!user) {
      navigate("/login");
      return;
    }

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await getWedding(id);
        if (data.redacted || (data.created_by && data.created_by !== user.id)) {
          setError("You do not have permission to edit this wedding.");
          setWedding(null);
          return;
        }
        setWedding(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Wedding not found");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, user, authLoading, navigate]);

  const handleSubmit = async (data: CreateWeddingPayload) => {
    if (!id) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const { data: updated } = await updateWedding(id, data);
      setWedding(updated);
      setSuccess("Wedding details saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update wedding");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;

    setPhotoError("");
    setPhotoSuccess("");
    try {
      if (isHeicLike(file)) {
        setPhotoPhase("converting");
      } else {
        setPhotoPhase("uploading");
      }
      const prepared = await prepareCouplePhoto(file);

      setPhotoPhase("uploading");
      const base64 = await fileToBase64(prepared.file);
      const { data } = await uploadCouplePhoto({
        wedding_id: id,
        filename: prepared.file.name,
        content_type: prepared.file.type || "image/jpeg",
        base64,
      });
      setWedding(data.wedding);
      setPhotoSuccess(
        prepared.convertedFromHeic
          ? "Couple photo converted from HEIC to JPEG and uploaded."
          : "Couple photo uploaded."
      );
    } catch (err) {
      setPhotoError(photoErrorMessage(err));
    } finally {
      setPhotoPhase("idle");
      e.target.value = "";
    }
  };

  if (authLoading || loading) return <Loading />;
  if (error && !wedding) return <ErrorMessage message={error} />;
  if (!wedding || !id) return null;

  const initial: Partial<CreateWeddingPayload> = {
    title: wedding.title,
    date: wedding.date,
    description: wedding.description,
    location: wedding.location,
    couple_name_1: wedding.couple_name_1 || undefined,
    couple_name_2: wedding.couple_name_2 || undefined,
    story: wedding.story || undefined,
    venue_name: wedding.venue_name || undefined,
    venue_address: wedding.venue_address || undefined,
    city: wedding.city || undefined,
    state: wedding.state || undefined,
    zip: wedding.zip || undefined,
    dress_code: wedding.dress_code || "",
    vibe_tags: wedding.vibe_tags || [],
    sale_close_date: wedding.sale_close_date || undefined,
    listing_status: wedding.listing_status,
    requires_21_plus: wedding.requires_21_plus,
    is_public: wedding.is_public,
    allow_ghost_seats: wedding.allow_ghost_seats,
    ticket_price: wedding.ticket_price ?? 0,
  };

  const photoBusy = photoPhase !== "idle";

  return (
    <div className="page edit-listing-page">
      <div className="edit-listing-sticky-bar">
        <Link to={`/weddings/${id}/manage`} className="btn btn-secondary btn-sm">
          Manage Guests &amp; Tiers
        </Link>
        <Link to={`/weddings/${id}`} className="btn btn-secondary btn-sm">
          View Public Page
        </Link>
      </div>

      <div className="edit-listing-body">
        <Link to="/my-weddings" className="back-link">
          &larr; Back to My Weddings
        </Link>

        <h1>Edit Wedding</h1>

        <WeddingForm
          key={`${wedding.id}-${wedding.listing_status}-${wedding.date}`}
          initial={initial}
          onSubmit={handleSubmit}
          loading={saving}
          submitError={error}
          submitSuccess={success}
          onClearSubmitFeedback={() => {
            setError("");
            setSuccess("");
          }}
        />

        <div className="photo-upload-box">
          <h2 className="profile-section-title">Couple Photo</h2>
          <p className="form-hint">Upload a photo to feature on your wedding listing.</p>
          <p className="form-hint">{COUPLE_PHOTO_ACCEPTED_LABEL}</p>
          {wedding.couple_photo_url && (
            <img
              src={wedding.couple_photo_url}
              alt="Couple"
              style={{
                maxWidth: "280px",
                width: "100%",
                marginBottom: "1rem",
                border: "1px solid var(--color-border-subtle)",
              }}
            />
          )}
          <div className="form-group">
            <label htmlFor="couple_photo">Photo file</label>
            <input
              id="couple_photo"
              type="file"
              accept={COUPLE_PHOTO_ACCEPT}
              disabled={photoBusy}
              onChange={handlePhotoUpload}
            />
            {photoPhase === "converting" && (
              <p className="form-hint" role="status" aria-live="polite">
                Converting HEIC to JPEG so it works in all browsers… This can take a
                moment for large photos.
              </p>
            )}
            {photoPhase === "uploading" && (
              <p className="form-hint" role="status" aria-live="polite">
                Uploading photo…
              </p>
            )}
            {photoError && (
              <p className="field-error" role="alert">
                {photoError}
              </p>
            )}
            {photoSuccess && (
              <p className="form-hint" role="status">
                {photoSuccess}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditWedding;
